import { useCallback, useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { AppContext } from 'app/AppContext'
import { getDefaultMovementDateRange } from 'utils/movementDateRange'

export const useMovimientos = (
  userId = undefined,
  type_identificador = 1,
  canFetch = true
) => {
  const { rolData } = useContext(AppContext)
  const [tableData, setTableData] = useState([])
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(null)

  const [bootstrapReady, setBootstrapReady] = useState(false)
  const [isDataFetching, setIsDataFetching] = useState(false)
  const [isFiltering, setIsFiltering] = useState(false)
  const [filterByUser, setFilterByUser] = useState([])

  const [fetchConfig, setFetchConfig] = useState({
    offset: 1,
    limit: 10,
    order: 'DESC'
  })

  const arrayArea = []
  const arraySector = []
  const arrayPlants = []

  const [filterValue, setFilterValue] = useState({
    area: [],
    ...getDefaultMovementDateRange(),
    status: [],
    keyword: '',
    sector: [],
    porterias: [],
    plants: []
  })

  const getPermissionUserNivelSectorZona = useCallback(async () => {
    if (!canFetch) {
      setBootstrapReady(false)
      setFilterValue({
        area: [],
        ...getDefaultMovementDateRange(),
        status: [],
        keyword: '',
        sector: [],
        porterias: [],
        plants: []
      })
      setFilterByUser({
        area: [],
        sector: [],
        plants: [],
        porterias: []
      })
      setIsDataFetching(false)
      return
    }

    try {
      setIsDataFetching(true)

      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL_AUTH}/access-web-user?userId=${rolData?.id}&specieId=1,2,3,4`
      )

      const array = []
      const array2 = []
      const array3 = []

      data?.allAccess?.forEach((item) => {
        if (item?.specie.name_specie === 'Pavos') {
          item.accessWebUserZones?.forEach((ite) => {
            const ids = ite.accessWebUserSector?.map((elem) => elem.sector_id)
            array2.push(...ids)
          })
          array.push('Pavos')
        }
        if (item?.specie.name_specie === 'Cerdos') {
          item.accessWebUserZones?.forEach((ite) => {
            const ids = ite.accessWebUserSector?.map((elem) => elem.sector_id)
            array2.push(...ids)
          })
          array.push('Cerdos')
        }
        if (item?.specie.name_specie === 'Pollos') {
          item.accessWebUserZones?.forEach((ite) => {
            const ids = ite.accessWebUserSector?.map((elem) => elem.sector_id)
            array2.push(...ids)
          })
          array.push('Pollos')
        }
        if (item?.specie.name_specie === 'Planta Alimentos') {
          array3.push(item?.plant_id)
          array.push('Planta Alimentos')
        }
      })

      const dataArrArea = new Set(array)
      const dataArrSector = new Set(array2)
      const dataArrPlants = new Set(array3)

      arrayArea.push(...dataArrArea)
      arraySector.push(...dataArrSector)
      arrayPlants.push(...dataArrPlants)

      const baseFilter = {
        area: arrayArea,
        ...getDefaultMovementDateRange(),
        status: [],
        keyword: '',
        sector: arraySector.sort((a, b) => a - b),
        porterias: data?.porteriaAccess ?? [],
        plants: arrayPlants.sort((a, b) => a - b)
      }

      setFilterValue(baseFilter)
      setFilterByUser({
        area: baseFilter.area,
        sector: baseFilter.sector,
        plants: baseFilter.plants,
        porterias: baseFilter.porterias
      })

      setBootstrapReady(true)
    } catch (err) {
      console.error(err)
      setError(err)
      setBootstrapReady(true) // aunque falle permisos, permite que el fetch intente
    } finally {
      setIsDataFetching(false)
    }
  }, [canFetch, rolData])

  useEffect(() => {
    getPermissionUserNivelSectorZona()
  }, [getPermissionUserNivelSectorZona])

  useEffect(() => {
    if (!bootstrapReady || !canFetch) return

    const fetchData = async () => {
      const { limit, order, offset } = fetchConfig
      setIsDataFetching(true)

      try {
        let response

        const search = String(filterValue?.keyword ?? '').trim()

        const area =
          filterValue?.area?.length > 0 ? '&area=' + filterValue.area : ''
        const dates =
          filterValue?.startDt && filterValue?.endDt
            ? '&startDt=' + filterValue.startDt + '&endDt=' + filterValue.endDt
            : ''
        const status =
          filterValue?.status?.length > 0
            ? '&status=' + filterValue.status
            : ''
        const sector =
          filterValue?.sector?.length > 0
            ? '&sector=' + filterValue.sector
            : ''
        const porteria =
          filterValue?.porterias?.length > 0
            ? '&porteria=' + filterValue.porterias
            : ''
        const planta =
          filterValue?.plants?.length > 0
            ? '&planta=' + filterValue.plants
            : ''

        if (!userId) {
          response = await axios.get(
            `${process.env.REACT_APP_BASE_URL_MOVEMENTS}/movements/find?order=${order}&pageNumber=${offset}&pageSize=${limit}&search=${encodeURIComponent(search)}` +
              `${area}${dates}${status}${sector}${porteria}${planta}`
          )
        } else {
          response = await axios.get(
            `${process.env.REACT_APP_BASE_URL_MOVEMENTS}/movements-user/user-movements?type_identificador=${type_identificador}&identificador=${encodeURIComponent(userId)}&pageNumber=${offset}&pageSize=${limit}&search=${encodeURIComponent(search)}` +
              `${area}${dates}${status}${sector}${porteria}${planta}`
          )
        }

        const data = response.data.list ?? []
        const countRows = response.data.count ?? 0

        setTotal(countRows)
        setTableData(data)
      } catch (err) {
        console.error(err)
        setError(err)
      } finally {
        setIsDataFetching(false)
      }
    }

    fetchData()
  }, [bootstrapReady, filterValue, fetchConfig, userId, type_identificador, canFetch])

  return {
    tableData,
    error,
    total,
    isDataFetching,
    setTableData,
    isFiltering,
    setIsFiltering,
    getPermissionUserNivelSectorZona,
    filterByUser,
    fetchConfig,
    setFetchConfig,
    filterValue,
    setFilterValue,
    setIsDataFetching
  }
}
